<template>
    <Wrapper :title="$t('revoke.accountRevoke')" @on-back="$emit('on-back', $options.name)">
        <template #content>
            <van-form ref="formRef" validate-first>
                <van-field
                    v-model="form.email"
                    name="email"
                    autocomplete="off"
                    :formatter="value => value.toLowerCase()"
                    :show-error="false"
                    :placeholder="$t('login.inputMailPlaceholder')"
                    :rules="[
                        {
                            required: true,
                            pattern: RegExps.emailReg,
                            message: $t('login.invalidEmailFormat'),
                        },
                    ]"
                />
                <van-field
                    v-model="form.captcha"
                    key="captcha"
                    name="captcha"
                    autocomplete="off"
                    :show-error="false"
                    :placeholder="$t('login.enterCaptcha')"
                    :rules="[
                        {
                            required: true,
                            message: $t('login.enterCaptcha'),
                        },
                    ]"
                />
            </van-form>
            <a
                href="javascript:void(0)"
                :class="[{ disabled: captchaDisabled }, 'float-right']"
                @click="handleCountdown"
            >
                {{ captchaText }}
            </a>
            <!-- 嵌入式天御验证码弹框 -->
            <div :class="['tc-wrapper', 'fixed', { hidden }]">
                <van-icon
                    name="close"
                    class="close absolute"
                    size="24"
                    color="var(--g-gray-3)"
                    @click="closeCaptcha"
                />
                <div id="tc-container" class="flex-center absolute-center" ref="tcContainer" />
            </div>
        </template>
        <template #footer>
            <van-button
                type="info"
                :class="['w-full', { hidden: !hidden }]"
                :loading="loading"
                @click="handleSubmit"
            >
                {{ $t('common.nextStep') }}
            </van-button>
        </template>
    </Wrapper>
</template>

<script>
import Vue from 'vue';
import { Button, Form, Field, Icon } from 'vant';
import useEmail from '@/mixins/useEmail';
import { RegExps } from '@/lib/utils';
import { EmailType } from '@/enums';
import { revokeCaptchaCheck } from '@/api/revoke';
import Wrapper from './Wrapper.vue';
import { Target } from '../constant';

Vue.use(Form);
Vue.use(Field);
Vue.use(Button);
Vue.use(Icon);

export default {
    name: 'RevokeAccount',
    mixins: [useEmail],
    components: {
        Wrapper,
    },
    props: {
        initialRevoke: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            RegExps,
            loading: false,
            form: {
                email: '',
                captcha: '',
            },
            hidden: true, // 天御验证码弹框的显示与隐藏
        };
    },
    mounted() {
        if (this.$refs.tcContainer) {
            this.$refs.tcContainer.addEventListener('click', this.stopPropagation, {
                capture: true,
            });
        }
    },
    beforeDestroy() {
        if (this.$refs.tcContainer) {
            this.$refs.tcContainer.removeEventListener('click', this.stopPropagation);
        }

        this.end();
    },
    methods: {
        setHidden(boolean) {
            this.hidden = boolean;
        },
        handleCountdown() {
            if (this.captchaDisabled) return;

            // prettier-ignore
            this.$refs.formRef.validate('email').then(() => {
                this.start(
                    this.form.email,
                    EmailType.RevokeAccount,
                    {
                        flowId: this.initialRevoke.flowId,
                        captchaOptions: {
                            type: 'embed',
                            getContainer: '#tc-container',
                            needFeedBack: false,
                        },
                        beforeCaptchaMount() {
                            this.setHidden(false);
                        },
                        onCaptchaSuccess() {
                            this.setHidden(true);
                        },
                        onCaptchaFailed() {
                            this.setHidden(true);
                        },
                        errorCaptchaCaptured() {
                            this.setHidden(true);
                        },
                    },
                );
            });
        },
        async handleSubmit() {
            await this.$refs.formRef.validate();

            // 校验是否获取验证码（防止用户乱填）
            if (this.form.captcha && !this.flowIdList[EmailType.RevokeAccount]) {
                return this.$toast(this.$t('login.obtainCaptcha'));
            }

            try {
                this.loading = true;
                const { nextAction, ...rest } = await revokeCaptchaCheck({
                    flowId: this.flowIdList[EmailType.RevokeAccount],
                    emailCode: this.form.captcha,
                    hash: this.emailMd5,
                });

                let target = '';
                switch (Number(nextAction)) {
                    case 0: // 直接注销成功，流程结束
                    case 3: // Android 来源，Google 账号注销完成，安卓 SDK 后续调用对应 revoke API
                        target = Target.Success;
                        break;
                    case 1: // 存在犹豫期，需在确认后完成注销登记
                        target = Target.Hesitation;
                        break;
                    case 2: // iOS 来源，走 apple revokeToken 流程，直接注销
                        target = Target.IOSRevalidate;
                        break;
                    default:
                        break;
                }
                this.$emit('on-step', {
                    current: Target.RevokeAccount,
                    target,
                    data: {
                        ...rest,
                        flowId: this.flowIdList[EmailType.RevokeAccount], // 其实就是传过去的注销流程的 flowId
                        action: Number(nextAction),
                    },
                });
            } finally {
                this.loading = false;
            }
        },
        closeCaptcha() {
            // 隐藏验证码弹框、隐藏关闭按钮、解禁“发送验证码”（如果没有开始倒计时）
            if (this.captchaInstance) this.captchaInstance.destroy();
            if (!this.timer) this.setDisabled(false);
            this.setHidden(true);
        },
        stopPropagation(e) {
            e.stopPropagation();
        },
    },
};
</script>

<style lang="scss" scoped>
.van-field.van-cell {
    background-color: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 5px !important;
    padding: 7px 14px;

    &:nth-child(2) {
        margin-bottom: var(--g-space-xs);
    }

    &::after {
        border-bottom: unset;
    }

    /deep/ input.van-field__control {
        --g-text-primary-color: var(--g-gray-3);
    }
}

.van-field.van-field--error /deep/ .van-field__error-message {
    --g-danger-color: var(--g-red-3);
}

a.disabled {
    opacity: var(--g-active-opacity);
}

.tc-wrapper {
    background-color: rgba(0, 0, 0, 0.4);
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;

    // 天御嵌入式的验证码弹框固定宽高为 300 * 230
    --tc-width: 300px;
    --tc-height: 230px;

    .close {
        top: calc((100vh - var(--tc-height)) / 2 - 30px);
        right: calc((100% - var(--tc-width)) / 2);
    }

    #tc-container {
        min-width: var(--tc-width);
        min-height: var(--tc-height);
    }
}

.hidden {
    visibility: hidden;
}

@media screen and (max-width: 300px) {
    .close {
        right: calc((100% - var(--tc-width)) / 2 + 10px);
    }
    #tc-container {
        transform: scale(0.9) translate(-55%, -50%);
    }
}
</style>
